import { defineComponent, computed, onMounted, onBeforeUnmount, ref, } from 'vue';
import { storeToRefs } from 'pinia';
import useTransactionsStore from '@/store/modules/transactions';
import LoaderComponent from '@/components/general/LoaderComponent.vue';
// Module-level state (outside Vue reactivity) to avoid DataCloneError
// when Peach SDK uses postMessage internally
let peachCheckoutInstance = null;
let onCheckoutComplete = null;
let onCheckoutStateChange = null;
export default defineComponent({
    name: 'EFTDepositComponent',
    components: {
        LoaderComponent,
    },
    emits: ['closeIframe'],
    setup(_, { emit }) {
        const transactionsStore = useTransactionsStore();
        const { collection } = storeToRefs(transactionsStore);
        const isLoadingCheckout = ref(false);
        const checkoutError = ref(null);
        const canRetry = ref(false);
        const extractCheckoutId = (paymentLink) => {
            if (!paymentLink)
                return null;
            try {
                const url = new URL(paymentLink);
                return url.searchParams.get('checkoutId');
            }
            catch {
                return null;
            }
        };
        const checkoutId = computed(() => extractCheckoutId(collection.value?.bankInfo.paymentLink));
        const isEmbeddedCheckout = computed(() => {
            const hasCheckoutId = !!checkoutId.value;
            const hasEntityId = !!process.env.VUE_APP_PEACH_ENTITY_ID;
            const isPeachLink = collection.value?.bankInfo.paymentLink?.includes('peachpayments');
            return hasCheckoutId && hasEntityId && isPeachLink;
        });
        const loadPeachScript = () => new Promise((resolve, reject) => {
            if (typeof Checkout !== 'undefined') {
                resolve();
                return;
            }
            const sdkUrl = process.env.VUE_APP_PEACH_SDK_URL;
            if (!sdkUrl) {
                reject(new Error('Peach SDK URL not configured'));
                return;
            }
            const script = document.createElement('script');
            script.src = sdkUrl;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Peach SDK'));
            document.head.appendChild(script);
        });
        const initializeCheckout = async () => {
            if (isLoadingCheckout.value)
                return;
            const entityId = process.env.VUE_APP_PEACH_ENTITY_ID;
            if (!checkoutId.value || !entityId) {
                checkoutError.value = 'Payment configuration error. Please try again.';
                return;
            }
            isLoadingCheckout.value = true;
            checkoutError.value = null;
            canRetry.value = false;
            // Set up callbacks that bridge to Vue state
            onCheckoutComplete = () => {
                emit('closeIframe');
            };
            onCheckoutStateChange = state => {
                checkoutError.value = state.error;
                canRetry.value = state.canRetry;
            };
            try {
                await loadPeachScript();
                // Store in plain variable (NOT in ref) to avoid DataCloneError
                peachCheckoutInstance = Checkout.initiate({
                    key: entityId,
                    checkoutId: checkoutId.value,
                    customisations: {
                        showCancelButton: true,
                        showAmountField: false,
                        theme: { brand: { primary: '#FBBC05' } },
                    },
                    eventHandlers: {
                        onCompleted: () => {
                            peachCheckoutInstance?.unmount();
                            peachCheckoutInstance = null;
                            onCheckoutComplete?.();
                        },
                        onCancelled: () => {
                            peachCheckoutInstance?.unmount();
                            peachCheckoutInstance = null;
                            onCheckoutStateChange?.({
                                error: 'Payment cancelled',
                                canRetry: true,
                            });
                        },
                        onExpired: () => {
                            peachCheckoutInstance?.unmount();
                            peachCheckoutInstance = null;
                            onCheckoutStateChange?.({
                                error: 'Payment session expired. Please start a new payment.',
                                canRetry: false,
                            });
                        },
                        onError: event => {
                            peachCheckoutInstance?.unmount();
                            peachCheckoutInstance = null;
                            console.error('Payment failed:', event);
                            onCheckoutStateChange?.({
                                error: 'Payment failed. Please try again.',
                                canRetry: true,
                            });
                        },
                    },
                });
                peachCheckoutInstance.render('#peach-payment-form');
            }
            catch (error) {
                console.error('Failed to initialize Peach checkout:', error);
                checkoutError.value =
                    'Failed to load payment form. Please try again or contact support.';
                canRetry.value = true;
            }
            finally {
                isLoadingCheckout.value = false;
            }
        };
        const retryCheckout = () => {
            initializeCheckout();
        };
        onMounted(() => {
            if (isEmbeddedCheckout.value) {
                initializeCheckout();
            }
        });
        onBeforeUnmount(() => {
            try {
                peachCheckoutInstance?.unmount();
            }
            catch (error) {
                console.error('Peach checkout cleanup failed:', error);
            }
            peachCheckoutInstance = null;
            onCheckoutComplete = null;
            onCheckoutStateChange = null;
        });
        return {
            collection,
            isEmbeddedCheckout,
            isLoadingCheckout,
            checkoutError,
            canRetry,
            retryCheckout,
        };
    },
});
